/**
 * GuidedTour — forced first-run walkthrough of the patient app.
 *
 * Experiment-gated: `guided-tour-v1` (audience: portal, backend-assigned
 * cohorts — Sanity doc `experiment-guided-tour-v1`, weights editable in
 * Studio). variant-a plays the tour; control (or a paused/absent doc)
 * goes straight into the app. Exposure (`$experiment_viewed`) fires for
 * BOTH cohorts at the moment the tour would have started, so the
 * comparison has a denominator.
 *
 * Plays once, right after onboarding completes and the patient lands on
 * /app Home (armed via localStorage in `useAcceptConsents`, see
 * `lib/tourFlags`). Opens on a centered welcome card, then spotlights
 * shell elements with a dimmed backdrop + floating tooltip: Home status
 * → Stack tab (click-through) → Stack page beat → Clinic (click-through;
 * on mobile: the ⋯ overflow button, then the Clinic row inside the
 * drawer) → Clinic page beat (message box) → Shop tab (click-through) →
 * single compounds → Protocols tab → protocols beat → "all set" modal.
 * Tab steps advance by the patient actually clicking the spotlighted
 * tab; each landing gets a page-context beat describing that screen's
 * key element before the next tab step.
 *
 * Mechanics:
 * - Targets are `data-tour="…"` attributes on shell elements (Sidebar,
 *   MobileBottomNav, OverflowDrawer, MarketplaceScreen, HomeScreen).
 *   The engine polls for the element so steps can span route changes.
 * - The backdrop is a banded rect decomposition of "viewport minus the
 *   target hole minus any `reveal` content cutouts" (dimRects). The
 *   target hole stays interactive for `advance: 'click'` steps (the real
 *   NavLink/button receives the click) and is covered by a blocker for
 *   `next` steps; reveal cutouts are visible but never interactive.
 * - Steps whose target never appears (e.g. Protocols tab when the
 *   shop-stacks experiment is off) auto-skip; a global 10s bail-out
 *   guarantees the forced tour can never brick the app.
 * - Mobile vs desktop step list is decided at start via the `md`
 *   breakpoint (850px, tailwind.config.ts).
 *
 * QA: append `?tour=1` to any /app route to replay (bypasses the
 * experiment cohort and fires no exposure event).
 */

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';

import { Button } from '@/design-system/primitives';
import { track } from '@/lib/analytics';
import { useBootstrap, useMyExperimentAssignments } from '@/lib/api/hooks';
import { useExperimentVariant } from '@/lib/experiments';
import { markTourDone, shouldAutoStartTour, tourUi } from '@/lib/tourFlags';

const TOUR_EXPERIMENT_KEY = 'guided-tour-v1';
const STACKS_EXPERIMENT_KEY = 'shop-stacks-v1';
const HOLE_PAD = 6;
const TOOLTIP_W = 300;
const OPTIONAL_TARGET_TIMEOUT_MS = 2_500;
const MISSING_TARGET_BAILOUT_MS = 10_000;

type TourStep = {
  id: string;
  /** Human-readable name for analytics payloads. */
  label: string;
  /** data-tour attribute value to spotlight. No target = centered card
   *  over a full dim (the welcome step). */
  target?: string;
  /** Heading for centered-card steps (welcome). */
  title?: string;
  body: string;
  placement?: 'top' | 'bottom' | 'right';
  advance: 'next' | 'click';
  /** Footer hint for click-to-advance steps. */
  hint?: string;
  /** Auto-skip silently if the target never appears (experiment-gated UI). */
  optional?: boolean;
  /** data-tour of associated CONTENT to un-dim alongside the target —
   *  e.g. the clinician card while the Clinic tab is spotlit. Matches
   *  multiple elements; purely visual (never clickable), best-effort. */
  reveal?: string;
  /** Pulse the flash on the primary hole (content targets, not buttons). */
  flashTarget?: boolean;
  onEnter?: () => void;
  /** Called (throttled) while the target is missing/offscreen — e.g. re-open
   *  the overflow drawer if the patient scrim-tapped it closed mid-step. */
  whileMissing?: () => void;
};

type Hole = { top: number; left: number; right: number; bottom: number };

type RunState = {
  steps: TourStep[];
  index: number;
  /** All steps done — showing the closing modal. */
  done: boolean;
  platform: 'desktop' | 'mobile';
};

function buildSteps(
  isDesktop: boolean,
  protocolsEnabled: boolean,
  ui: { openOverflow: () => void; closeOverflow: () => void },
): TourStep[] {
  const welcome: TourStep = {
    id: 'welcome',
    label: 'Welcome',
    title: 'Welcome to System Labs.',
    body: 'This is your patient portal, where everything about your treatment lives. Let’s take a quick look around. It takes less than a minute.',
    advance: 'next',
  };
  const home: TourStep = {
    id: 'home',
    label: 'Homepage overview',
    target: 'home-status',
    body: 'This is your homepage. Any outstanding tasks show up here, along with the status of your order.',
    placement: 'bottom',
    advance: 'next',
    reveal: 'home-order-status',
  };
  // Page-context beats — after each tab click lands on a new screen,
  // describe that screen's key element before moving to the next tab.
  const stackPage: TourStep = {
    id: 'stack-page',
    label: 'Stack page',
    target: 'stack-overview',
    body: 'All of your treatments live here. Dosing instructions appear once your clinician writes your script.',
    placement: 'bottom',
    advance: 'next',
    optional: true,
    flashTarget: true,
  };
  const support: TourStep = {
    id: 'support',
    label: 'Customer support',
    target: 'support-button',
    body: 'If you have questions about your order status, deliveries, or anything non-clinical, you can go here.',
    placement: 'top',
    advance: 'next',
    optional: true,
  };
  const clinicPage: TourStep = {
    id: 'clinic-page',
    label: 'Message your clinician',
    target: 'clinic-composer',
    body: 'Send your clinician a message anytime right here. No appointment needed.',
    placement: 'bottom',
    advance: 'next',
    optional: true,
    flashTarget: true,
  };
  const shopProducts: TourStep = {
    id: 'shop-products',
    label: 'Single compounds',
    target: 'shop-products-tab',
    body: 'Browse specific compounds here…',
    placement: 'bottom',
    advance: 'next',
    reveal: 'shop-products-grid',
  };
  const shopProtocols: TourStep = {
    id: 'shop-protocols',
    label: 'Protocols tab',
    target: 'shop-protocols-tab',
    body: '…or complete protocols.',
    placement: 'bottom',
    advance: 'click',
    hint: isDesktop
      ? 'Click Protocols to continue'
      : 'Tap Protocols to continue',
    optional: true,
  };
  const protocolsInfo: TourStep = {
    id: 'protocols-info',
    label: 'Protocols overview',
    target: 'shop-protocols-grid',
    body: 'Protocols are curated stacks of compounds designed to work together toward one goal. Add the whole bundle to your stack in one tap.',
    placement: 'top',
    advance: 'next',
    optional: true,
    flashTarget: true,
  };

  if (isDesktop) {
    return [
      welcome,
      home,
      {
        id: 'stack',
        label: 'Stack tab',
        target: 'stack-tab',
        body: 'This is where you can see all of the treatments in your stack.',
        placement: 'right',
        advance: 'click',
        hint: 'Click Stack to continue',
      },
      stackPage,
      support,
      {
        id: 'clinic',
        label: 'Clinic tab',
        target: 'clinic-tab',
        body: 'You can also connect with your clinician.',
        placement: 'right',
        advance: 'click',
        hint: 'Click Clinic to continue',
      },
      clinicPage,
      {
        id: 'shop',
        label: 'Shop tab',
        target: 'shop-tab',
        body: "If you're interested in other compounds, you can find them in the Shop.",
        placement: 'right',
        advance: 'click',
        hint: 'Click Shop to continue',
      },
      shopProducts,
      ...(protocolsEnabled ? [shopProtocols, protocolsInfo] : []),
    ];
  }

  return [
    welcome,
    home,
    {
      id: 'stack',
      label: 'Stack tab',
      target: 'stack-tab-m',
      body: 'This is where you can see all of the treatments in your stack.',
      placement: 'top',
      advance: 'click',
      hint: 'Tap Stack to continue',
    },
    stackPage,
    support,
    {
      id: 'more',
      label: 'More menu',
      target: 'more-button',
      body: 'You can also connect with your clinician from the Clinic. Tap here to open the menu.',
      placement: 'top',
      advance: 'click',
      hint: 'Tap ⋯ to continue',
    },
    {
      id: 'clinic',
      label: 'Clinic tab',
      target: 'clinic-row',
      body: 'Open the Clinic.',
      placement: 'bottom',
      advance: 'click',
      hint: 'Tap Clinic to continue',
      whileMissing: ui.openOverflow,
    },
    clinicPage,
    {
      id: 'shop',
      label: 'Shop tab',
      target: 'shop-tab-m',
      body: "If you're interested in other compounds, you can find them in the Shop.",
      placement: 'top',
      advance: 'click',
      hint: 'Tap Shop to continue',
      onEnter: ui.closeOverflow,
    },
    shopProducts,
    ...(protocolsEnabled ? [shopProtocols, protocolsInfo] : []),
  ];
}

function sameHole(a: Hole | null, b: Hole | null): boolean {
  if (!a || !b) return a === b;
  return (
    Math.abs(a.top - b.top) < 1 &&
    Math.abs(a.left - b.left) < 1 &&
    Math.abs(a.right - b.right) < 1 &&
    Math.abs(a.bottom - b.bottom) < 1
  );
}

function sameHoles(a: Hole[], b: Hole[]): boolean {
  return a.length === b.length && a.every((h, i) => sameHole(h, b[i]));
}

/**
 * Decompose "viewport minus N holes" into dim rectangles via horizontal
 * banding: split the viewport at every hole edge, then within each band
 * emit the x-segments not covered by any hole active in that band.
 */
function dimRects(
  vw: number,
  vh: number,
  holes: Hole[],
): { top: number; left: number; width: number; height: number }[] {
  const ys = Array.from(
    new Set([0, vh, ...holes.flatMap((h) => [h.top, h.bottom])]),
  )
    .filter((y) => y >= 0 && y <= vh)
    .sort((a, b) => a - b);
  const rects: { top: number; left: number; width: number; height: number }[] =
    [];
  for (let i = 0; i < ys.length - 1; i++) {
    const top = ys[i];
    const bottom = ys[i + 1];
    if (bottom - top < 0.5) continue;
    const active = holes
      .filter((h) => h.top < bottom && h.bottom > top)
      .sort((a, b) => a.left - b.left);
    let x = 0;
    for (const h of active) {
      const l = Math.max(h.left, 0);
      const r = Math.min(h.right, vw);
      if (l > x)
        rects.push({ top, left: x, width: l - x, height: bottom - top });
      x = Math.max(x, r);
    }
    if (x < vw)
      rects.push({ top, left: x, width: vw - x, height: bottom - top });
  }
  return rects;
}

function tooltipStyle(step: TourStep, hole: Hole): CSSProperties {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const width = Math.min(TOOLTIP_W, vw - 24);
  const centerX = (hole.left + hole.right) / 2;
  const clampedLeft = Math.min(
    Math.max(centerX - width / 2, 12),
    vw - width - 12,
  );

  let placement = step.placement ?? 'bottom';
  if (placement === 'right' && vw - hole.right < width + 24)
    placement = 'bottom';
  if (placement === 'bottom' && vh - hole.bottom < 190) placement = 'top';
  // Only flip top→bottom when there actually IS room below — a
  // viewport-height hole has room on neither side and falls through to
  // the float-over-the-hole fallback in the 'top' branch.
  if (placement === 'top' && hole.top < 190 && vh - hole.bottom >= 190)
    placement = 'bottom';

  if (placement === 'right') {
    return {
      width,
      left: hole.right + 12,
      top: Math.min(Math.max(hole.top - 6, 12), vh - 190),
    };
  }
  if (placement === 'top') {
    if (hole.top < 190) {
      // Viewport-height hole (e.g. the protocols grid) — no outside room
      // at all, so float the card near the bottom, over the hole.
      return { width, left: clampedLeft, bottom: 70 };
    }
    // Anchor via `bottom` so the card grows upward. Never position with a
    // CSS translateY here — framer-motion's y animation owns `transform`
    // and would overwrite it (the card then renders DOWN off-screen).
    return { width, left: clampedLeft, bottom: vh - hole.top + 12 };
  }
  return { width, left: clampedLeft, top: hole.bottom + 12 };
}

export function GuidedTour({
  blocked = false,
  openOverflow,
  closeOverflow,
}: {
  /** The welcome video must finish/dismiss before even a QA-forced tour. */
  blocked?: boolean;
  openOverflow: () => void;
  closeOverflow: () => void;
}) {
  const { data: bootstrap } = useBootstrap();
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const stacksVariant = useExperimentVariant(STACKS_EXPERIMENT_KEY);
  // Cohort straight from the assignments query (not ExperimentsContext) so
  // we can tell "still loading" (null → wait) apart from "loaded but not
  // assigned" (→ control, fail safe-off). A failed query also reads as
  // control so a fresh onboardee is never left in limbo.
  const { data: myAssignments, isError: assignmentsError } =
    useMyExperimentAssignments();
  const tourVariant: string | null = assignmentsError
    ? 'control'
    : myAssignments?.assignments
      ? (myAssignments.assignments[TOUR_EXPERIMENT_KEY] ?? 'control')
      : null;

  const [run, setRun] = useState<RunState | null>(null);
  const [hole, setHole] = useState<Hole | null>(null);
  // Secondary content cutouts (step.reveal) — visual only, never clickable.
  const [revealHoles, setRevealHoles] = useState<Hole[]>([]);

  const patientId = bootstrap?.patient.id ?? null;
  const step = run && !run.done ? run.steps[run.index] : null;

  // Keep imperative helpers in refs so the polling loop / listeners always
  // see the latest state without re-subscribing.
  const uiRef = useRef({ openOverflow, closeOverflow });
  uiRef.current = { openOverflow, closeOverflow };

  const protocolsEnabledRef = useRef(stacksVariant === 'variant-a');
  protocolsEnabledRef.current = stacksVariant === 'variant-a';

  function makeSteps(isDesktop: boolean): TourStep[] {
    return buildSteps(isDesktop, protocolsEnabledRef.current, {
      openOverflow: () => uiRef.current.openOverflow(),
      closeOverflow: () => uiRef.current.closeOverflow(),
    });
  }
  const makeStepsRef = useRef(makeSteps);
  makeStepsRef.current = makeSteps;

  // Analytics payload for the step a user action happened on. Step 0 =
  // the welcome card; numbered steps match the visible "STEP x OF n".
  function stepEventParams(s: TourStep | null, r: RunState | null) {
    if (!s || !r) return {};
    const numbered = r.steps.filter((st) => st.target);
    return {
      step_number: s.target ? numbered.indexOf(s) + 1 : 0,
      step: s.id,
      step_title: s.label,
      total_steps: numbered.length,
      platform: r.platform,
    };
  }

  function begin(startedVia: 'auto' | 'qa_param') {
    const isDesktop = window.matchMedia('(min-width: 850px)').matches;
    const steps = makeStepsRef.current(isDesktop);
    tourUi.start();
    track('onboarding_tutorial_initiated', {
      platform: isDesktop ? 'desktop' : 'mobile',
      total_steps: steps.filter((s) => s.target).length,
      started_via: startedVia,
    });
    setRun({
      steps,
      index: 0,
      done: false,
      platform: isDesktop ? 'desktop' : 'mobile',
    });
  }
  const beginRef = useRef(begin);
  beginRef.current = begin;

  function finish(kind: 'completed' | 'skipped') {
    if (patientId) markTourDone(patientId);
    tourUi.stop();
    if (kind === 'completed') {
      track('onboarding_tutorial_complete', {
        platform: run?.platform,
        total_steps: run ? run.steps.filter((s) => s.target).length : undefined,
      });
    } else {
      track('onboarding_tutorial_skip', stepEventParams(step, run));
    }
    setRun(null);
  }

  function advance() {
    setRun((r) => {
      if (!r || r.done) return r;
      const next = r.index + 1;
      if (next >= r.steps.length) return { ...r, done: true };
      return { ...r, index: next };
    });
  }
  const advanceRef = useRef(advance);
  advanceRef.current = advance;

  /** User-driven advance (Next button / clicking the spotlit target) —
   *  fires the funnel event with the step being completed. Auto-skips of
   *  missing optional targets go through advance() directly, untracked. */
  function advanceByUser(from: TourStep | null) {
    track('onboarding_tutorial_next_step', stepEventParams(from, run));
    advance();
  }
  const advanceByUserRef = useRef(advanceByUser);
  advanceByUserRef.current = advanceByUser;

  // ---- start conditions ----------------------------------------------
  const started = Boolean(run);
  const isHome = location.pathname === '/app' || location.pathname === '/app/';
  const forced = searchParams.get('tour') === '1';
  const onboardingState = bootstrap?.onboarding_state;
  useEffect(() => {
    if (blocked || started || !patientId) return;
    if (forced) {
      const next = new URLSearchParams(searchParams);
      next.delete('tour');
      setSearchParams(next, { replace: true });
      if (!isHome) navigate('/app', { replace: true });
      beginRef.current('qa_param');
      return;
    }
    if (!isHome || onboardingState !== 'complete') return;
    if (!shouldAutoStartTour(patientId)) return;
    if (tourVariant === null) return; // cohort not resolved yet
    // Exposure for BOTH cohorts at the moment the tour would have started —
    // the armed-and-landed population is the experiment's denominator.
    track('$experiment_viewed', {
      experiment_key: TOUR_EXPERIMENT_KEY,
      variant_key: tourVariant,
      source: 'guided_tour',
    });
    if (tourVariant !== 'variant-a') {
      // Control never sees the tour — mark done so the pending flag (and
      // AppLayout's post-purchase-upsell hold) is released for good.
      markTourDone(patientId);
      return;
    }
    // Small delay so Home paints (and the welcome→/app redirect settles)
    // before the backdrop drops in.
    const t = setTimeout(() => beginRef.current('auto'), 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [blocked, started, patientId, forced, isHome, onboardingState, tourVariant]);

  // If the layout unmounts mid-tour (sign-out), release the running flag —
  // pending stays set, so the tour re-arms on the next Home visit.
  useEffect(() => () => tourUi.stop(), []);

  // Rebuild the step list if the viewport crosses the md boundary mid-run
  // (devtools device emulation, tablet rotation). The frozen begin()-time
  // list would otherwise spotlight nav elements that don't exist at the
  // new width — an unfillable target and a permanent full dim. Steps are
  // remapped by id; the mobile-only `more` step falls back to `clinic`.
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 850px)');
    const onChange = () => {
      setRun((r) => {
        if (!r) return r;
        const platform = mq.matches ? 'desktop' : 'mobile';
        if (platform === r.platform) return r;
        const steps = makeStepsRef.current(mq.matches);
        if (r.done) return { ...r, steps, platform };
        const currentId = r.steps[r.index]?.id;
        let index = steps.findIndex((s) => s.id === currentId);
        if (index === -1) index = steps.findIndex((s) => s.id === 'clinic');
        if (index === -1) index = Math.min(r.index, steps.length - 1);
        return { steps, index, done: false, platform };
      });
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // ---- target tracking -------------------------------------------------
  useEffect(() => {
    if (!step) {
      setHole(null);
      setRevealHoles([]);
      return;
    }
    step.onEnter?.();
    setRevealHoles([]);
    if (!step.target) {
      // Centered-card step (welcome) — full dim, no spotlight to track.
      setHole(null);
      return;
    }
    let missingSince: number | null = null;
    let lastMissingNudge = 0;
    let scrolledIntoView = false;

    const measure = () => {
      const el = document.querySelector<HTMLElement>(
        `[data-tour="${step.target}"]`,
      );
      const r = el?.getBoundingClientRect();
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      // The overflow drawer stays mounted offscreen when closed, so an
      // in-viewport check (not mere DOM presence) decides visibility.
      const visible =
        el &&
        r &&
        r.width > 0 &&
        r.height > 0 &&
        r.bottom > 0 &&
        r.top < vh &&
        r.right > 0 &&
        r.left < vw;
      if (visible) {
        missingSince = null;
        if (!scrolledIntoView) {
          scrolledIntoView = true;
          // Fixed-position targets (bottom nav, drawer rows) have a null
          // offsetParent — scrollIntoView on those scrolls the document to
          // their out-of-flow DOM position, yanking the page around for no
          // visual change. Only scroll targets that live in the flow.
          if (el.offsetParent !== null) {
            if (r.height > vh * 0.7) {
              // Taller-than-viewport target (protocols grid): centering
              // scrolls past its top and the dim vanishes entirely. Bring
              // its top into view only if it isn't already.
              if (r.top < 70 || r.top > vh * 0.6) {
                window.scrollBy({ top: r.top - 100, behavior: 'smooth' });
              }
            } else if (r.top < 70 || r.bottom > vh - 70) {
              el.scrollIntoView({ block: 'center', behavior: 'smooth' });
            }
          }
        }
        const next: Hole = {
          top: Math.max(r.top - HOLE_PAD, 0),
          left: Math.max(r.left - HOLE_PAD, 0),
          right: Math.min(r.right + HOLE_PAD, vw),
          bottom: Math.min(r.bottom + HOLE_PAD, vh),
        };
        setHole((prev) => (sameHole(prev, next) ? prev : next));
        // Associated-content cutouts — best-effort, only while the primary
        // target is up. Cap at 3 matches to bound the mask math.
        if (step.reveal) {
          const reveals: Hole[] = [];
          const els = Array.from(
            document.querySelectorAll<HTMLElement>(
              `[data-tour="${step.reveal}"]`,
            ),
          ).slice(0, 3);
          for (const relEl of els) {
            const rr = relEl.getBoundingClientRect();
            const revealVisible =
              rr.width > 0 &&
              rr.height > 0 &&
              rr.bottom > 0 &&
              rr.top < vh &&
              rr.right > 0 &&
              rr.left < vw;
            if (revealVisible) {
              reveals.push({
                top: Math.max(rr.top - HOLE_PAD, 0),
                left: Math.max(rr.left - HOLE_PAD, 0),
                right: Math.min(rr.right + HOLE_PAD, vw),
                bottom: Math.min(rr.bottom + HOLE_PAD, vh),
              });
            }
          }
          setRevealHoles((prev) => (sameHoles(prev, reveals) ? prev : reveals));
        }
        return;
      }
      setHole((prev) => (prev === null ? prev : null));
      setRevealHoles((prev) => (prev.length === 0 ? prev : []));
      const now = Date.now();
      missingSince ??= now;
      if (
        step.whileMissing &&
        now - missingSince > 600 &&
        now - lastMissingNudge > 1_500
      ) {
        lastMissingNudge = now;
        step.whileMissing();
      }
      if (
        (step.optional && now - missingSince > OPTIONAL_TARGET_TIMEOUT_MS) ||
        now - missingSince > MISSING_TARGET_BAILOUT_MS
      ) {
        advanceRef.current();
      }
    };

    measure();
    const iv = setInterval(measure, 150);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      clearInterval(iv);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [step]);

  // ---- click-to-advance -------------------------------------------------
  useEffect(() => {
    if (!step || !step.target || step.advance !== 'click') return;
    const onClick = (e: MouseEvent) => {
      const el = document.querySelector(`[data-tour="${step.target}"]`);
      if (el && e.target instanceof Node && el.contains(e.target)) {
        // Let the click's own effect land first (navigation / drawer open /
        // tab switch) so the next step's target exists when we advance.
        setTimeout(() => advanceByUserRef.current(step), 200);
      }
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [step]);

  if (!run) return null;

  // Welcome (targetless) doesn't count toward the visible step numbers.
  const numbered = run.steps.filter((s) => s.target);
  const stepNo = step?.target ? numbered.indexOf(step) + 1 : 0;
  const showDim = true;

  return (
    <div
      className="fixed inset-0 z-[110] pointer-events-none"
      aria-live="polite"
    >
      {/* Dimmed backdrop — four rects around the spotlight hole so the hole
          itself stays interactive for click-to-advance steps. */}
      {showDim && !run.done && hole && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.2 }}
          className="absolute inset-0"
        >
          {/* Dim = viewport minus the target hole minus any associated-
              content reveals (banded rect decomposition). */}
          {dimRects(window.innerWidth, window.innerHeight, [
            hole,
            ...revealHoles,
          ]).map((d, i) => (
            <div
              key={i}
              className="absolute bg-black/60 pointer-events-auto"
              style={{
                top: d.top,
                left: d.left,
                width: d.width,
                height: d.height,
              }}
            />
          ))}
          {/* Content flash — pulses dim→clear when the step lands, only on
              content targets (never plain buttons/tabs). Keyed by step so
              it restarts each step; pointer-events-none throughout. */}
          {step?.flashTarget && (
            <div
              key={`flash-${step.id}`}
              className="absolute bg-black/60 pointer-events-none tour-hole-flash"
              style={{
                top: hole.top,
                left: hole.left,
                width: hole.right - hole.left,
                height: hole.bottom - hole.top,
              }}
            />
          )}
          {/* Associated-content reveals: faint outline + a blocker so the
              revealed region is visible but not interactive (no flash —
              the steady un-dim is the highlight). */}
          {revealHoles.map((rh, i) => (
            <div key={`reveal-${step?.id ?? 'none'}-${i}`}>
              <div
                className="absolute border border-white/60 pointer-events-none"
                style={{
                  top: rh.top,
                  left: rh.left,
                  width: rh.right - rh.left,
                  height: rh.bottom - rh.top,
                }}
              />
              <div
                className="absolute pointer-events-auto"
                style={{
                  top: rh.top,
                  left: rh.left,
                  width: rh.right - rh.left,
                  height: rh.bottom - rh.top,
                }}
              />
            </div>
          ))}
          {/* Spotlight ring */}
          <div
            className={
              step?.advance === 'click'
                ? 'absolute border-2 border-white animate-pulse'
                : 'absolute border-2 border-white'
            }
            style={{
              top: hole.top,
              left: hole.left,
              width: hole.right - hole.left,
              height: hole.bottom - hole.top,
            }}
          />
          {/* Block interaction inside the hole on informational steps. */}
          {step?.advance === 'next' && (
            <div
              className="absolute pointer-events-auto"
              style={{
                top: hole.top,
                left: hole.left,
                width: hole.right - hole.left,
                height: hole.bottom - hole.top,
              }}
            />
          )}
        </motion.div>
      )}
      {/* Full dim while the target is being located or on the done modal. */}
      {showDim && (run.done || !hole) && (
        <div className="absolute inset-0 bg-black/60 pointer-events-auto" />
      )}

      {/* Step tooltip */}
      <AnimatePresence mode="wait">
        {step && hole && (
          <motion.div
            key={step.id}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="absolute pointer-events-auto bg-raised border border-line-subtle shadow-modal p-15 flex flex-col gap-10"
            style={tooltipStyle(step, hole)}
            role="dialog"
            aria-label={`Tour step ${stepNo} of ${numbered.length}`}
          >
            <div className="type-label-sm text-ink-muted">
              STEP {stepNo} OF {numbered.length}
            </div>
            <p className="type-body-md text-ink">{step.body}</p>
            <div className="flex items-center justify-between gap-10 pt-5">
              <button
                type="button"
                onClick={() => finish('skipped')}
                className="type-label-sm text-ink-muted hover:text-ink underline-offset-4 hover:underline"
              >
                Skip tour
              </button>
              {step.advance === 'next' ? (
                <Button variant="primary" onClick={() => advanceByUser(step)}>
                  Next
                </Button>
              ) : (
                <span className="type-label-sm text-accent text-right">
                  {step.hint}
                </span>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Welcome card — centered over a full dim, softens the drop-in
          before the first spotlight step. */}
      <AnimatePresence>
        {step && !step.target && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex items-center justify-center px-15 pointer-events-auto"
          >
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-[36rem] bg-raised border border-line-subtle shadow-modal p-25 flex flex-col gap-15 text-center"
            >
              <div className="type-label-sm text-ink-muted">QUICK TOUR</div>
              <h2 className="type-title-md text-ink">{step.title}</h2>
              <p className="type-body-md text-ink-secondary">{step.body}</p>
              <Button
                variant="primary"
                className="w-full justify-center"
                onClick={() => advanceByUser(step)}
              >
                Take the tour
              </Button>
              <button
                type="button"
                onClick={() => finish('skipped')}
                className="type-label-sm text-ink-muted hover:text-ink underline-offset-4 hover:underline"
              >
                Skip for now
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* All-done modal */}
      <AnimatePresence>
        {run.done && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex items-center justify-center px-15 pointer-events-auto"
          >
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.2 }}
              className="w-full max-w-[36rem] bg-raised border border-line-subtle shadow-modal p-25 flex flex-col gap-15 text-center"
            >
              <div className="type-label-sm text-ink-muted">TOUR COMPLETE</div>
              <h2 className="type-title-md text-ink">You're all set.</h2>
              <p className="type-body-md text-ink-secondary">
                That's everything you need to get started. Your homepage always
                has your latest tasks and order updates, and your clinician is one
                message away in the Clinic.
              </p>
              <Button
                variant="primary"
                className="w-full justify-center"
                onClick={() => finish('completed')}
              >
                Done
              </Button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
